import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchMovies, mapMovieForCard, SHOWTIMES } from '../api/starviewApi';
import { useAuth } from '../auth/AuthContext';
import { useCountry } from '../context/CountryContext';
import './Showtimes1.css';
import { localizeDigits } from '../utils/localPricing';

function isPastTime(slotTime) {
  const today = new Date();
  const [time, period] = slotTime.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  const slotDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
  return slotDate < today;
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const Showtimes1 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const { isAuthenticated } = useAuth();
  const { country, countries } = useCountry();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMovieId, setExpandedMovieId] = useState(null);
  // Seeded from ?q= if we arrived from Header2's search bar, so that search
  // actually goes somewhere real instead of being a dead form.
  const [search, setSearch] = useState(() => query.get('q') || '');
  const cardRefs = useRef({});

  // "All Countries" (country === '') fetches the full, unfiltered catalog -
  // every real film across every theater, not just the currently selected
  // one. Booking still works correctly per-film either way since
  // TheaterRoom/assignAuditorium always use that specific movie's own
  // country/theaterNumber, never the header's selection.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMovies({ size: 100, country })
      .then((rawMovies) => {
        if (cancelled) return;
        setMovies(rawMovies.map(mapMovieForCard));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [country]);

  const countryInfo = (code) => countries.find((c) => c.code === code);

  // If we arrived from a "Get Tickets" link with a specific movie in mind
  // (e.g. from the hero), auto-expand and scroll to that movie's card.
  useEffect(() => {
    const requestedId = query.get('movieId');
    if (!requestedId || movies.length === 0) return;
    setExpandedMovieId(requestedId);
    const el = cardRefs.current[requestedId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movies]);

  const [dayOffset, setDayOffset] = React.useState(0);
  const dayLabel = (off) => {
    const d = new Date(); d.setDate(d.getDate() + off);
    if (off === 0) return t('showtimes.today');
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const handlePickTime = (movie, time) => {
    const d = new Date(); d.setDate(d.getDate() + dayOffset);
    const dateKey = d.toISOString().slice(0, 10);
    const destination = `/TheaterRoom?movieId=${encodeURIComponent(movie.id)}&time=${encodeURIComponent(time)}&date=${dateKey}`;
    if (!isAuthenticated) {
      navigate('/Login', { state: { from: destination } });
      return;
    }
    navigate(destination, { state: { movie } });
  };

  const filteredMovies = movies.filter((m) =>
    m.title.toLowerCase().includes(search.trim().toLowerCase())
  );

  if (loading) {
    return (
      <div className="showtimes-page">
        <div className="showtimes-header">
          <h1 className="showtimes-heading">{t('showtimes.heading')}</h1>
        </div>
        <div className="showtimes-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="movie-card movie-card-skeleton" />
          ))}
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="showtimes-page showtimes-status">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="showtimes-page">
      <div className="showtimes-header">
        <div>
          <h1 className="showtimes-heading">{t('showtimes.heading')}</h1>
          <p className="showtimes-subheading">
            {t('showtimes.today')} • {countryInfo(country)?.flag} {countryInfo(country)?.label || t('showtimes.allCountries')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 14px' }} role="tablist" aria-label="Choose a day">
          {[0,1,2,3,4,5,6].map((off) => (
            <button key={off} role="tab" aria-selected={dayOffset === off} onClick={() => setDayOffset(off)}
              style={{ padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                       border: '1px solid rgba(255,255,255,0.3)',
                       background: dayOffset === off ? '#CFAA41' : 'transparent',
                       color: dayOffset === off ? '#141021' : 'inherit' }}>
              {dayLabel(off)}
            </button>
          ))}
        </div>
        <div className="showtimes-search">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M11.5 11.5L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder={t('showtimes.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filteredMovies.length === 0 && (
        <p className="showtimes-empty">{t('showtimes.noMoviesMatch', { query: search })}</p>
      )}

      <div className="showtimes-grid">
        {filteredMovies.map((movie) => {
          const isExpanded = expandedMovieId === movie.id;
          return (
            <div
              key={movie.id}
              ref={(el) => { cardRefs.current[movie.id] = el; }}
              className={`movie-card${isExpanded ? ' expanded' : ''}`}
            >
              <div className="movie-card-poster">
                {movie.image_url ? (
                  <img src={movie.image_url} alt={movie.title} loading="lazy" />
                ) : (
                  <div className="movie-card-poster-fallback" />
                )}
                {!country && (
                  <span className="movie-card-country-badge">
                    {countryInfo(movie.country)?.flag || '🌐'} {countryInfo(movie.country)?.label || movie.country}
                  </span>
                )}
                <div className="movie-card-poster-fade" />
              </div>
              <div className="movie-card-body">
                <h2 className="movie-card-title">{movie.title}</h2>
                <p className="movie-card-meta">
                  {[movie.rating, movie.runtime].filter(Boolean).join('  •  ')}
                </p>
                <button
                  className="movie-card-showtimes-btn"
                  onClick={() => setExpandedMovieId(isExpanded ? null : movie.id)}
                >
                  {isExpanded ? t('showtimes.hideShowtimes') : t('showtimes.viewShowtimes')}
                  <svg
                    className={`chevron${isExpanded ? ' up' : ''}`}
                    width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <div className={`movie-card-times${isExpanded ? ' open' : ''}`}>
                  <div className="movie-card-times-inner">
                    {SHOWTIMES.map((time) => {
                      const disabled = dayOffset === 0 && isPastTime(time);
                      return (
                        <button
                          key={time}
                          className={`time-chip${disabled ? ' disabled' : ''}`}
                          disabled={disabled}
                          onClick={() => handlePickTime(movie, time)}
                        >
                          {localizeDigits(time, country)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Showtimes1;
